import { Sheet } from '../../../../components/Sheet/Sheet';
import editIcon from '../../../../icons/Pencil.svg';
import closeIcon from '../../../../icons/X.svg';
import * as translator from '../../../../utils/translator';
import DeliveryAddressPersonalAddressForm from './DeliveryAddressPersonalAddressForm';
import { CustomerAddressCreation, defaultCustomerAddressCreationValues } from './schemas';
import DeliveryAddressPackstationForm from './DeliveryAddressPackstationForm';
import { useEffect, useMemo, useState } from 'react';
import Select from '../../../../components/Select/Select';

const ADDRESS_FORM_ID = "address-form";
const PACKSTATION_FORM_ID = "packstation-form";

interface AddressFormSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: CustomerAddressCreation) => void;
  language: string;
  country: string;
  addressAttributes: CustomerAddressCreation | null;
  isNewAddress: boolean;
  isMobile?: boolean;
  supportPackstation?: boolean;
}

const AddressFormSheet = ({ isOpen, onClose, onSubmit, language, addressAttributes, isNewAddress, isMobile, country, supportPackstation }: AddressFormSheetProps) => {
  const titleKey = isNewAddress ? 'DeliveryAddressV2/addNewAddress' : 'DeliveryAddressV2/editAddress';
  const buttonTextKey = isNewAddress ? 'DeliveryAddressV2/addNewAddress' : 'DeliveryAddressV2/saveChanges';
  const [formID, setFormID] = useState<"address-form" | "packstation-form">(ADDRESS_FORM_ID);
  const [formData, setFormData] = useState<CustomerAddressCreation | null>({...defaultCustomerAddressCreationValues, country_id: country.toUpperCase()});
  // Reset form on mount
  useEffect(() => {
    // When component mounts, initialize with the right values
    if (!addressAttributes) {
      setFormID(ADDRESS_FORM_ID);
      setFormData({...defaultCustomerAddressCreationValues, country_id: country.toUpperCase()});
    } else if (addressAttributes?.address_type === 'packstation') {
      setFormID(PACKSTATION_FORM_ID);
      setFormData({...addressAttributes, address_type: 'packstation'});
    } else {
      setFormID(ADDRESS_FORM_ID);
      setFormData({...addressAttributes, address_type: 'regular'});
    }
  }, []); // Empty dependency array means this only runs once on mount

  // Create country options for the Select component based on the language
  const countryOptions = useMemo(() => {
    if (country.toLowerCase() === "de") {
      return [
        { id: "de", value: translator.translate(language, "shipping", "ShippingForm/country_name/de") },
      ];
    } else if (country.toLowerCase() === "ch") {
      return [
        { id: "ch", value: translator.translate(language, "shipping", "ShippingForm/country_name/ch") }
      ];
    } else if (country.toLowerCase() === "gb") {
      return [
        { id: "gb", value: translator.translate(language, "shipping", "ShippingForm/country_name/gb") }
      ];
    } else {
      return [
        { id: "fr", value: translator.translate(language, "shipping", "ShippingForm/country_name/fr") },
      ];
    }
  }, [language]);

  // Create select options for form type
  const formIDOptions = useMemo(() => {
    const options = [{ id: ADDRESS_FORM_ID, value: translator.translate(language, "my_account", "DeliveryAddressV2/personalAddress") || "Personal Address" }];
    if (supportPackstation) {
      options.push({ id: PACKSTATION_FORM_ID, value: translator.translate(language, "my_account", "DeliveryAddressV2/packstation") || "Packstation" });
    }
    return options;
  }, [language, supportPackstation]);

  // Handle form type change
  const handleFormTypeChange = (data: { option: any, index: number }) => {
    setFormID(data.option.id as "address-form" | "packstation-form");
    setFormData({ ...(formData || defaultCustomerAddressCreationValues), address_type: data.option.id === ADDRESS_FORM_ID ? "regular" : "packstation" });
  };

  return (
    <Sheet isOpen={isOpen} onClose={onClose}>

      <Sheet.Header className="px-3 md:px-4">
        <div id="address-header" className="flex justify-between items-center border-0 border-b border-[#E5E5E5] border-solid p-4 md:p-8">
          <div id="address-title-container" className="flex gap-x-1 items-center">
            <img src={editIcon} className='w-[18px] h-[18px] md:w-[24px] md:h-[24px]' alt="Edit" />
            <h1 id="address-modal-title" className="text-base md:text-xl font-normal text-gray-700">
              {translator.translate(language, "my_account", titleKey)}
            </h1>
          </div>
          {!isMobile && <button
            id="address-modal-close"
            className='w-[24px] h-[24px] bg-transparent border-none hover:cursor-pointer'
            onClick={onClose}
          >
            <img className='w-[20px] h-[20px] md:w-[24px] md:h-[24px]' src={closeIcon} alt="Close" />
          </button>}
        </div>
      </Sheet.Header>

      <Sheet.Body className="flex flex-col items-center px-3 md:px-4">

        {/* Address Type Select */}
        <div id="address-form-type-select-container" className='px-0 md:px-4 w-full max-w-md mx-auto my-4'>
          <Select
            id="form-type-select"
            options={formIDOptions as never[]}
            initialValue={formIDOptions.find(option => option.id === formID)}
            onChangeCallback={handleFormTypeChange}
            placeholder={translator.translate(language, "my_account", "DeliveryAddressV2/selectAddressType") || "Select address type"}
            errorMode={false}
            style={{
              border: "1px solid #E5E5E5",
              height: "48px",
              width: "100%",
              borderRadius: "6px",
              backgroundColor: "white",
              margin: "0px"
            }}
          />
        </div>


        {/* Personal Address Form */}
        {formID === ADDRESS_FORM_ID && (
          <DeliveryAddressPersonalAddressForm
            onSubmit={onSubmit}
            formID={formID}
            language={language}
            defaultFormValues={isNewAddress ? { ...defaultCustomerAddressCreationValues, country_id: country.toUpperCase() } : formData as CustomerAddressCreation}
            countryOptions={countryOptions}
          />
        )}

        {/* Packstation Form */}
        {formID === PACKSTATION_FORM_ID && (
          <DeliveryAddressPackstationForm
            onSubmit={onSubmit}
            formID={formID}
            language={language}
            defaultFormValues={isNewAddress ? { ...defaultCustomerAddressCreationValues, country_id: country.toUpperCase() } : formData as CustomerAddressCreation}
            countryOptions={countryOptions}
          />
        )}

      </Sheet.Body>
      <Sheet.Footer className="md:pb-0 px-3 md:px-4">
        <div id="address-actions" className="flex gap-x-4 p-4 md:p-8">
          <button
            id="address-cancel"
            type="button"
            onClick={onClose}
            className="p-3 cursor-pointer w-full transition border border-[#269C6E] border-solid text-[#269C6E] rounded-[12px] hover:text-[#269C6E]/80 font-semibold focus:outline-none focus:ring-2 focus:ring-[#269C6E] bg-white  text-xs md:text-base"
          >
            {translator.translate(language, "my_account", `DeliveryAddressV2/cancel`)}
          </button>
          <button
            form={formID}
            id="address-save"
            type="submit"
            className="px-4 py-2 cursor-pointer w-full rounded-[12px] transition border-solid border-[#269C6E] bg-[#269C6E]  text-white font-semibold hover:bg-[#269C6E]/80 focus:outline-none focus:ring-2 focus:ring-[#269C6E]  text-xs md:text-base"
          >
            {translator.translate(language, "my_account", buttonTextKey)}
          </button>
        </div>
      </Sheet.Footer>
    </Sheet>
  );
};

export default AddressFormSheet; 